import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Renders a teammate-published tool embedded inside Run. Appends embed=1 (the
// sandbox template hides its own chrome) plus the viewer's session tokens so the
// tool's AuthGate signs them in silently — same handoff the Hub uses.
export default function PublishedToolPage() {
  const { toolId } = useParams();
  const [loading, setLoading] = useState(true);
  const [tool, setTool] = useState(null);
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setTool(null);
      setSrc(null);
      if (!supabase) {
        setLoading(false);
        return;
      }
      const { data } = await supabase
        .from('published_tools')
        .select('*')
        .eq('id', toolId)
        .eq('status', 'published')
        .maybeSingle();
      if (!alive) return;
      if (!data) {
        setLoading(false);
        return;
      }
      let url = data.url;
      try {
        const u = new URL(data.url);
        u.searchParams.set('embed', '1');
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          u.searchParams.set('hub_token', session.access_token);
          u.searchParams.set('hub_refresh', session.refresh_token);
        }
        url = u.toString();
      } catch {
        // malformed URL — embed it as stored
      }
      if (!alive) return;
      setTool(data);
      setSrc(url);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [toolId]);

  if (loading) {
    return <div style={{ padding: 24, color: '#737373' }}>Loading tool…</div>;
  }
  if (!tool) {
    return <div style={{ padding: 24, color: '#737373' }}>This tool isn’t available anymore.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 8px)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          borderBottom: '1px solid #e5e5e5',
          background: '#fff',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: '#171717' }}>{tool.name}</span>
          {tool.owner_name && (
            <span style={{ marginLeft: 8, fontSize: 13, color: '#a3a3a3' }}>
              built by {tool.owner_name}
            </span>
          )}
        </div>
        <a
          href={src}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: '#737373',
            textDecoration: 'none',
          }}
        >
          Open in new tab <ExternalLink size={14} />
        </a>
      </div>
      <iframe
        title={tool.name}
        src={src}
        style={{ flex: 1, width: '100%', border: 0, background: '#FAFAF8' }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
      />
    </div>
  );
}
